import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
// NB: Import from `/max` to use full metadata for validation.
// The default import uses minimal metadata and may allow invalid numbers. Do not remove.
import type { Country } from "react-phone-number-input/max";
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input/max";

import { Controller, useFormContext } from "@ui/components/Form";
import { InputWrapper } from "@ui/components/Inputs/components/InputWrapper";
import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";

import { CountrySelectWithIcon } from "./CountrySelectWithIcon";
import { useAvailableCountries, useCountryTranslations } from "./hooks";

import styles from "./InternationalTelephone.module.scss";

export type InternationalTelephoneProps = {
  id?: string;
  showLabel?: boolean;
  label?: string;
  fieldName?(name: string): string;
  isRequired?: boolean;
  isDisabled?: boolean;
  disclaimer?: string;
  placeholder?: string;
  isLocked?: boolean;
  mode?: "standard" | "editable";
  editButtonLabel?: string;
  onEditClick?: () => void;
  defaultCountry?: Country;
  autoComplete?: string;
  showErrorOnBlur?: boolean;
};

export const InternationalTelephone: FC<InternationalTelephoneProps> = ({
  id,
  showLabel = true,
  label,
  fieldName = (name) => name,
  isRequired = true,
  isDisabled = false,
  disclaimer,
  placeholder,
  mode,
  editButtonLabel,
  onEditClick,
  isLocked,
  defaultCountry,
  autoComplete,
  showErrorOnBlur = false,
}) => {
  const { __ } = useTranslations();
  const config = useConfig();
  const availableCountriesInDropdown = useAvailableCountries();
  const countryLabels = useCountryTranslations(availableCountriesInDropdown);
  const [isEditable, setIsEditable] = useState<boolean>(mode === "editable");
  const { control } = useFormContext();

  useEffect(() => {
    setIsEditable(mode === "editable");
  }, [mode]);

  const validDefaultCountry = useMemo(() => {
    if (defaultCountry && availableCountriesInDropdown.includes(defaultCountry)) {
      return defaultCountry;
    }
    return undefined;
  }, [defaultCountry, availableCountriesInDropdown]);

  const shouldShowInternationalOption = !validDefaultCountry;

  const handlePhoneValue = (phone: string) => {
    if (phone) {
      if (phone.startsWith("+")) {
        return phone;
      }
      return `+${phone}`;
    }
    return "";
  };

  const validatePhoneNumber = (value: string | undefined): true | string => {
    if (value) {
      if (!isValidPhoneNumber(handlePhoneValue(value))) {
        return __("internationalPhone.validation.invalid_format");
      }
      const phoneNumberCountry = parsePhoneNumber(value)?.country;
      if (phoneNumberCountry && !availableCountriesInDropdown.includes(phoneNumberCountry)) {
        return __("internationalPhone.validation.country_not_allowed");
      }
    }
    return true;
  };

  const handleOnEditClick = () => {
    setIsEditable(false);
    onEditClick?.();
  };

  const isDisclaimerVisible = !config.hideInternationalPhoneDisclaimer;

  return (
    <div className={styles.phoneContainer}>
      <Controller
        name={id || fieldName("internationalPhone")}
        control={control}
        rules={{
          required: isRequired ? __("internationalPhone.validation.field_required") : false,
          validate: validatePhoneNumber,
        }}
        render={({ field, fieldState: { error, isTouched } }) => {
          const visibleError = !showErrorOnBlur || isTouched ? error : undefined;

          return (
            <InputWrapper
              htmlFor={id}
              errorText={visibleError?.message}
              helperText={isDisclaimerVisible ? disclaimer : undefined}
              isError={!!visibleError}
              isOptional={!isRequired}
              labelId={`label-${id}`}
              label={(showLabel && label) || __("internationalPhone.label")}
              isHelperTextVisible={isDisclaimerVisible || !!visibleError}
              wrapperClassName={classNames(styles.wrapper, {
                [styles.isLocked]: isEditable || isLocked,
              })}
            >
              <PhoneInput
                {...field}
                international
                aria-labelledby={`label-${id}`}
                value={handlePhoneValue(field.value)}
                labels={countryLabels}
                countries={availableCountriesInDropdown}
                disabled={isDisabled}
                readOnly={isEditable || isLocked}
                placeholder={placeholder}
                limitMaxLength
                defaultCountry={validDefaultCountry}
                addInternationalOption={shouldShowInternationalOption}
                autoComplete={autoComplete || (isDisabled ? "off" : undefined)}
                countrySelectComponent={(props) => (
                  <CountrySelectWithIcon
                    {...props}
                    mode={mode}
                    editButtonLabel={editButtonLabel}
                    handleOnEditClick={handleOnEditClick}
                    isEditable={isEditable}
                    hasDefaultCountry={!!validDefaultCountry}
                  />
                )}
                className={classNames(styles.phoneInputContainer, {
                  [styles.hasError]: visibleError,
                  [styles.disabled]: isDisabled,
                  [styles.locked]: isEditable || isLocked,
                })}
              />
            </InputWrapper>
          );
        }}
      />
    </div>
  );
};
